import { useCMSPageField } from '@/cms/fields'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import { createDataLayerEvent } from '@/scopes/products/pages/components/Legacy/PracticalInformation/helpers/CreateDataLayerEvent'
import {
  Accordion,
  Collapsible,
  CollapsibleRoot,
  CollapsibleTrigger,
  SpaceBetween,
  tk,
  Typography,
} from '@sunwebgroup/ui-components'
import { useState } from 'react'
import { styled } from 'styled-components'

export interface MealplansProps {
  title: string
}

interface MealplansItemProps {
  title: string
  description?: string | null
}

const ALL_INCLUSIVES = [
  'all inclusive',
  'all-inclusive',
  'ultra all inclusive',
  'ultra-all-inclusive',
  'ultra all-inclusive',
  'extra all inclusive',
  'extra-all-inclusive',
  'extra all-inclusive',
  'tout compris',
  'tous compris',
]

const StyledMealplansContainer = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
`

const MealplansDescription = styled(Typography)`
  margin-bottom: ${tk('spacing_md')};
  & :first-child {
    margin-top: 0;
    padding-top: 0;
  }
  & :last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }
`

const StyledMealplansItem = styled.li`
  border: 2px solid ${tk('color_semantic_border')};
  border-radius: ${tk('radius_large')};
  margin-top: ${tk('spacing_xs')};
  box-shadow: ${tk('shadow_md_bottom')};
`

const StyledMealplansItemSubtitle = styled(Typography)`
  color: ${tk('color_semantic_text_strong')};
`

const StyledMealplansItemCollapsibleTrigger = styled(CollapsibleTrigger)`
  margin-top: ${tk('spacing_2xs')};

  ${Typography} {
    color: ${tk('color_semantic_text_strong')};
    text-decoration: underline;
  }
`

const StyledMealplansItemCollapsible = styled(Collapsible)`
  padding: ${tk('spacing_xs')} 0;

  p,
  ul {
    margin-top: unset; // Reset margin due to the default styling in MVC
    margin-bottom: ${tk('spacing_xs')};
  }
  li {
    list-style-type: disc;
  }
`

export function Mealplans({ title: mainTitle }: MealplansProps) {
  const { sendEvent } = useAnalytics()
  const mealplaninfo = useCMSPageField('PDP.mealplans')

  const items = mealplaninfo?.mealPlans

  function handleCollapsibleOpenChange(isOpen: boolean) {
    if (isOpen) {
      sendEvent(createDataLayerEvent({ element: 'Mealplans', isOpen: true }))
    }
    else {
      sendEvent(createDataLayerEvent({ element: 'Mealplans', isOpen: false }))
    }
  }

  // This is needed to get the correct description for the mealplan based on the type
  function getDescription(title: string) {
    if (ALL_INCLUSIVES.includes(title.toLowerCase())) {
      return mealplaninfo?.allInclusiveDescription
    }
    return undefined
  }

  if (!items || items.length === 0) {
    return null
  }

  return (
    <Accordion.Item title={mainTitle} icon="IconCutlery" onOpenChange={isOpen => handleCollapsibleOpenChange(isOpen)}>
      {mealplaninfo.description && (
        <MealplansDescription
          $variant="body-md"
          as="div"
          dangerouslySetInnerHTML={{ __html: mealplaninfo.description }}
        />
      )}
      <StyledMealplansContainer>
        {items.map((mealplan, index) => (
          <MealplansItem
            key={index}
            title={mealplan.type.description}
            description={getDescription(mealplan.type.description)}
          />
        ))}
      </StyledMealplansContainer>
    </Accordion.Item>
  )
}

function MealplansItem({ title, description }: MealplansItemProps) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()

  const [open, setOpen] = useState(false)

  const openLabel = intl.formatMessage({ id: 'show_more' })
  const closeLabel = intl.formatMessage({ id: 'show_less' })
  const subtitleId = title.toLowerCase().replace(/[\- /]/g, '_')
  const subTitle = intl.formatMessage({ id: 'accommodation_page_mealplan_item_subtitle' }, { id: subtitleId })

  function handleReadMoreOpenChange(itemLabel: string) {
    setOpen(!open)
    if (!open) {
      sendEvent(
        createPDPClickEvent({
          element: 'Read More',
          description: `Mealplan info`,
          result: itemLabel.toLowerCase(),
        }),
      )
    }
  }

  return (
    <StyledMealplansItem>
      <SpaceBetween direction="y" padding={tk('spacing_lg')}>
        <Typography $variant="label">{title}</Typography>
        <StyledMealplansItemSubtitle $variant="body-md">{subTitle}</StyledMealplansItemSubtitle>
        {description && (
          <CollapsibleRoot onOpenChange={() => handleReadMoreOpenChange(title)}>
            <StyledMealplansItemCollapsible>
              <Typography $variant="body-md" dangerouslySetInnerHTML={{ __html: description }} />
            </StyledMealplansItemCollapsible>
            <StyledMealplansItemCollapsibleTrigger>
              <Typography $variant="body-md-bold">{open ? closeLabel : openLabel}</Typography>
            </StyledMealplansItemCollapsibleTrigger>
          </CollapsibleRoot>
        )}
      </SpaceBetween>
    </StyledMealplansItem>
  )
}
